import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Percent, RotateCcw } from 'lucide-react';
import { supabase } from '@/lib/supabase';

interface PaymentTypeRow {
  id: string;
  name: string;
  driver_payable_percent: number | null;
  base_commission_percent: number | null;
  applies_to_driver_commission: boolean | null;
}

interface OverrideRow {
  payment_type_id: string;
  payable_percent: number;
}

export interface DriverPaymentTypePercentagesHandle {
  save: () => Promise<void>;
}

interface Props {
  driverName: string;
  tenantId: string | null | undefined;
  // The commission % that applies by default to commission-based payment types
  effectiveCommissionPercent: number | null;
  // Whether the selected driver type uses payment-type % rules at all
  enabled: boolean;
}

/**
 * Lets an admin set an individual driver-payable / commission % for each payment type
 * against this specific driver's profile. Overrides the company-wide payment type %.
 */
const DriverPaymentTypePercentages = forwardRef<DriverPaymentTypePercentagesHandle, Props>(
  ({ driverName, tenantId, effectiveCommissionPercent, enabled }, ref) => {
    const [paymentTypes, setPaymentTypes] = useState<PaymentTypeRow[]>([]);
    const [overrides, setOverrides] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);

    const load = async () => {
      setLoading(true);
      try {
        let ptQuery = supabase
          .from('payment_types')
          .select('id, name, driver_payable_percent, base_commission_percent, applies_to_driver_commission')
          .order('name');
        // Include both this tenant's payment types AND any global (null-tenant)
        // payment types such as "TM Hoist". Previously a strict tenant filter
        // hid global types from the driver modal, so the Hoist never appeared.
        if (tenantId) ptQuery = ptQuery.or(`tenant_id.eq.${tenantId},tenant_id.is.null`);
        const { data: ptData } = await ptQuery;
        setPaymentTypes((ptData as PaymentTypeRow[]) || []);


        const { data: ovData } = await supabase
          .from('driver_payment_type_percentages')
          .select('payment_type_id, payable_percent')
          .eq('driver_name', driverName);

        const map: Record<string, string> = {};
        ((ovData as OverrideRow[]) || []).forEach((o) => {
          map[o.payment_type_id] = String(o.payable_percent);
        });
        setOverrides(map);
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      if (driverName) load();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [driverName, tenantId]);

    // Company default % for a payment type, before any per-driver override.
    // Priority: payment type Standard % (base_commission_percent set in Settings)
    // -> the driver's flat effective commission % -> legacy driver_payable_percent.
    const defaultPercentFor = (pt: PaymentTypeRow): number | null => {
      if (
        pt.base_commission_percent !== null &&
        pt.base_commission_percent !== undefined
      ) {
        return Number(pt.base_commission_percent);
      }
      if (effectiveCommissionPercent !== null && effectiveCommissionPercent !== undefined) {
        return effectiveCommissionPercent;
      }
      if (pt.driver_payable_percent !== null && pt.driver_payable_percent !== undefined) {
        return Number(pt.driver_payable_percent);
      }
      return null;
    };


    const setOverride = (paymentTypeId: string, value: string) => {
      setOverrides((prev) => ({ ...prev, [paymentTypeId]: value }));
    };

    const clearOverride = (paymentTypeId: string) => {
      setOverrides((prev) => {
        const next = { ...prev };
        delete next[paymentTypeId];
        return next;
      });
    };

    useImperativeHandle(ref, () => ({
      save: async () => {
        // Build the set of rows to upsert and the ones to delete (cleared).
        const toUpsert: any[] = [];
        const toDeleteIds: string[] = [];

        paymentTypes.forEach((pt) => {
          const raw = overrides[pt.id];
          if (raw === undefined || raw === '') {
            toDeleteIds.push(pt.id);
            return;
          }
          const num = Number(raw);
          if (Number.isNaN(num)) {
            toDeleteIds.push(pt.id);
            return;
          }
          toUpsert.push({
            tenant_id: tenantId || null,
            driver_name: driverName,
            payment_type_id: pt.id,
            payable_percent: num,
            updated_at: new Date().toISOString(),
          });
        });

        if (toUpsert.length > 0) {
          const { error } = await supabase
            .from('driver_payment_type_percentages')
            .upsert(toUpsert, { onConflict: 'driver_name,payment_type_id' });
          if (error) throw error;
        }

        if (toDeleteIds.length > 0) {
          const { error } = await supabase
            .from('driver_payment_type_percentages')
            .delete()
            .eq('driver_name', driverName)
            .in('payment_type_id', toDeleteIds);
          if (error) throw error;
        }
      },
    }));

    if (!enabled) {
      return (
        <div className="rounded-lg border border-dashed border-gray-700 bg-gray-800/30 p-4 text-xs text-gray-400">
          The selected driver type does not use payment-type payable % rules, so per-payment-type
          commission rates are not applied for this driver. Switch to a commission/turnover-based
          driver type to set individual rates per payment type.
        </div>
      );
    }

    return (
      <div className="rounded-lg border border-gray-700 bg-gray-950/40 p-4">
        <div className="flex items-center gap-2 mb-1">
          <Percent className="w-4 h-4 text-yellow-500" />
          <div className="text-sm font-semibold text-yellow-500">Per Payment Type Commission %</div>
        </div>
        <p className="text-xs text-gray-400 mb-3">
          Set the individual commission / driver-payable % for each payment type for this driver.
          Leave blank to use the company default shown. Useful when some items (e.g. TM Subsidy,
          Discounts) are not paid at the standard {effectiveCommissionPercent ?? '50'}%.
        </p>

        {loading ? (
          <div className="py-4 text-center text-xs text-gray-500">Loading payment types…</div>
        ) : paymentTypes.length === 0 ? (
          <div className="py-4 text-center text-xs text-gray-500">
            No payment types found. Add payment types under Settings → Payment Types first.
          </div>
        ) : (
          <div className="overflow-x-auto rounded border border-gray-800">
            <table className="w-full text-sm">
              <thead className="bg-gray-800/80 text-gray-300">
                <tr>
                  <th className="text-left px-3 py-2">Payment Type</th>
                  <th className="text-right px-3 py-2 w-32">Company Default</th>
                  <th className="text-right px-3 py-2 w-40">This Driver %</th>
                  <th className="text-center px-3 py-2 w-28">Effective</th>
                  <th className="px-3 py-2 w-10" />
                </tr>
              </thead>
              <tbody>
                {paymentTypes.map((pt) => {
                  const def = defaultPercentFor(pt);
                  const raw = overrides[pt.id];
                  const hasOverride = raw !== undefined && raw !== '';
                  const effective = hasOverride && !Number.isNaN(Number(raw)) ? Number(raw) : def;
                  return (
                    <tr key={pt.id} className="border-t border-gray-800">
                      <td className="px-3 py-2 text-gray-200">{pt.name}</td>
                      <td className="px-3 py-2 text-right text-gray-400">
                        {def === null ? '—' : `${def}%`}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={raw ?? ''}
                          onChange={(e) => setOverride(pt.id, e.target.value)}
                          placeholder={def === null ? 'e.g. 50' : `Default ${def}%`}
                          className="text-right h-8"
                        />
                      </td>
                      <td className="px-3 py-2 text-center">
                        {effective === null || effective === undefined ? (
                          <span className="text-gray-500 text-xs">N/A</span>
                        ) : (
                          <Badge
                            className={
                              hasOverride
                                ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/40'
                                : 'bg-gray-700 text-gray-300'
                            }
                          >
                            {effective}%
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {hasOverride && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            title="Clear override (use company default)"
                            onClick={() => clearOverride(pt.id)}
                          >
                            <RotateCcw className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-[11px] text-gray-500 mt-2">
          These per-payment-type rates are saved when you click Save Changes. Highlighted Effective
          values indicate a custom rate that differs from the company default.
        </p>
      </div>
    );
  }
);

DriverPaymentTypePercentages.displayName = 'DriverPaymentTypePercentages';

export default DriverPaymentTypePercentages;
